// PKGV.A2 (#793) — <PKGNeighbourhoodPanel />, embedded in DecisionsPage
// and RisksPage's existing inline-expand rows (Sprint-A2-recon.md L1: no
// per-entity host route exists for either entity, so this is not a
// standalone page — it's a section inside an already-conditionally-
// rendered block).
//
// Design locked at V1-V4 adjudication (Sprint-A2-impl-prompt.md):
//   V1 — panel owns its own collapse, default collapsed, lazy-fetches on
//        first open. This is deliberate: the host row's own expand does
//        NOT trigger a fetch, so opening several rows on a list page
//        never fires several subgraph queries (§8 item 2, fetch-on-
//        expand N+1).
//   V2 — clicking a node re-anchors AND re-fetches (not a client-side-
//        only relayout) so exploration reveals genuinely new neighbours.
//        Same-node clicks are a no-op (loop guard). A reset control
//        returns to the original anchor — no back-stack, one level only.
//   V3 — the static flag check lives HERE (return null when off), not
//        at the DecisionsPage/RisksPage call sites, so both hosts' JSX
//        stays identical regardless of flag state (AC7 + S1).
//   V4 — data comes from fetchSubgraph(), A2's own seam over B1's
//        /pkg/subgraph (unmerged as of this story — see fetchSubgraph.ts).
//
// AC8 AMENDED post-V-adjudication (2026-07-19): defaults to
// `interactionLevel: 'interactive'`, not `'readonly'`. Discovered mid-
// implementation by reading PKGCanvas.tsx directly (not trusting the
// AC): `readonly` is a hard gate — `onNodeClick` is wired to a no-op
// when `interactionLevel === 'readonly'` (PKGCanvas.tsx:205-208,
// confirmed by A1's own callback test suite). V2's entire re-center
// feature depends on onNodeClick firing, so the original 'readonly'
// default (locked at Q10, before V2 existed) would have shipped this
// component with a core interaction that silently does nothing under
// its own default. 'interactive' only enables callbacks — no mutation
// surface, so it costs nothing relative to Q10's actual concern (not
// leaking toward D1's 'editable' territory). Prop stays overridable
// for a genuinely static consumer. Operator-adjudicated; S2 holds
// (this is A2's own component default, not a change to PKGCanvas or
// packages/shared).

import { useCallback, useState, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { PKGNode, InteractionLevel } from '@blueprint/shared';
import { fetchSubgraph, type FetchedSubgraph } from './fetchSubgraph';
import { isFeatureEnabled } from '../../config/featureFlags';
import { PKGFilterableNeighbourhood } from './PKGFilterableNeighbourhood';
import { clearFilterStateFromUrl } from './pkgFilterUrlState';

export interface PKGNeighbourhoodPanelProps {
  projectId: string;
  /**
   * pkg_nodes.id for the anchor entity — sourced from T2's pkgNodeId field
   * on the /decisions and /risks list responses (#1783, merged e12d6d03).
   * That field is typed `string | null` on the wire (no PKG node exists
   * yet for this entity), so both `null` and `undefined` mean "nothing to
   * anchor on" here. Renders nothing in either case — never a crash,
   * never a fetch attempt with a bad id (§7, §8 item 5).
   */
  anchorNodeId: string | null | undefined;
  /** Defaults to 'interactive' — see the AC8 amendment note above. */
  interactionLevel?: InteractionLevel;
}

type Status = 'collapsed' | 'loading' | 'empty' | 'unavailable' | 'ready';

const DEPTH = 2;

export function PKGNeighbourhoodPanel({
  projectId,
  anchorNodeId,
  interactionLevel = 'interactive',
}: PKGNeighbourhoodPanelProps) {
  const [status, setStatus] = useState<Status>('collapsed');
  // R1 adversarial-pass note (LOW, currently unreachable — not fixed):
  // seeded once from anchorNodeId and never re-synced if that PROP later
  // changes on an already-mounted instance. Not exploitable today —
  // DecisionsPage/RisksPage fetch their list once per mount, no polling
  // — but would silently pin the panel to a stale anchor if a future
  // change adds refetch/polling to a host page while a row is expanded.
  const [currentAnchorId, setCurrentAnchorId] = useState<string | null | undefined>(anchorNodeId);
  const [data, setData] = useState<FetchedSubgraph | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  // PKGV.A3 (#794) — R2 adversarial-pass fix (item 4): tracks whether the
  // anchor has EVER changed, one-way (never reset back to false). Clicking
  // "Back to original node" (handleReset) returns currentAnchorId to
  // anchorNodeId, which would make a NAIVE `currentAnchorId !==
  // anchorNodeId` check ("isReanchored") false again — re-triggering
  // PKGFilterableNeighbourhood's restoreFromUrl=true on the RETURN trip
  // and re-exposing the exact setSearchParams-commit-timing race this
  // component already fixed for the OUTBOUND re-anchor. A one-way flag
  // is the only correct signal: URL-restore is a nice-to-have for the
  // single pristine first view of this row, never for any subsequent
  // remount, regardless of which anchor id it happens to land back on.
  const [hasEverReanchored, setHasEverReanchored] = useState(false);
  // PKGV.A3 (#794) — R1/R3 adversarial-pass fix: a THIRD sibling of the
  // setSearchParams-timing bug class, same root shape (a synchronous
  // handler makes a state change that a still-in-flight async operation
  // is unaware of and unconditionally overwrites once it resolves).
  // The "Show graph"/"Hide graph" toggle button is rendered
  // unconditionally (not gated on status), so it's clickable while
  // status === 'loading'. Collapsing during a fetch used to leave the
  // pending load() with no way to know the user had since collapsed —
  // its eventual setData/setStatus would silently reopen the panel the
  // user had just closed. A monotonically-increasing request id, bumped
  // both at the start of every load() call and explicitly on collapse,
  // lets a resolved fetch recognize it's been superseded and no-op
  // instead of applying stale results. This is pre-existing A2 machinery
  // (load/handleToggle predate A3), but A3 already touches this exact
  // branch (clearFilterUrlState below) and the bug was only actually
  // caught during this story's own adversarial pass, so it's fixed here
  // rather than left for a separate story to rediscover.
  const requestIdRef = useRef(0);

  const load = useCallback(async (anchorId: string) => {
    const requestId = ++requestIdRef.current;
    setStatus('loading');
    try {
      const result = await fetchSubgraph(projectId, anchorId, DEPTH);
      if (requestIdRef.current !== requestId) return; // superseded — collapsed, or a newer load() started
      setData(result);
      setStatus(result.edges.length === 0 ? 'empty' : 'ready');
    } catch {
      if (requestIdRef.current !== requestId) return;
      // 403 and 500 collapse into one generic message — no existence
      // oracle (V-locked S3 / §7: unauthorized must not read differently
      // from any other failure).
      setStatus('unavailable');
    }
  }, [projectId]);

  // PKGV.A3 (#794) — clears the panel's filter/search URL params
  // synchronously, in the SAME handler that changes currentAnchorId (and
  // therefore PKGFilterableNeighbourhood's key). This must live HERE, not
  // in the child's own unmount effect: an unmount-time cleanup in the
  // OUTGOING child runs during commit, which is not guaranteed to happen
  // before the INCOMING child's render-phase lazy useState initializer
  // reads the URL — that ordering gap let a just-unchecked filter survive
  // into the "reset" remount as a real, observed bug (caught by the V1
  // re-anchor-resets-filters test, not just reasoned about). Clearing
  // synchronously here, batched with setCurrentAnchorId in the same
  // handler, guarantees the child never observes stale params.
  const clearFilterUrlState = useCallback(() => {
    const next = new URLSearchParams(searchParams);
    clearFilterStateFromUrl(next);
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  const handleToggle = () => {
    if (!anchorNodeId) return;
    if (status === 'collapsed') {
      setCurrentAnchorId(anchorNodeId);
      void load(anchorNodeId);
    } else {
      // Invalidate any in-flight load() explicitly — if nothing is
      // pending this is a no-op bump, but if the user collapsed WHILE
      // status === 'loading', this stops that request's eventual
      // setData/setStatus from silently reopening the panel they just
      // closed (R1/R3 finding — see requestIdRef's declaration above).
      requestIdRef.current++;
      setStatus('collapsed');
      clearFilterUrlState();
    }
  };

  const handleNodeClick = useCallback((node: PKGNode) => {
    if (node.id === currentAnchorId) return; // loop guard (§8 item 3)
    clearFilterUrlState();
    setHasEverReanchored(true);
    setCurrentAnchorId(node.id);
    void load(node.id);
  }, [currentAnchorId, load, clearFilterUrlState]);

  const handleReset = () => {
    if (!anchorNodeId) return;
    clearFilterUrlState();
    // hasEverReanchored deliberately NOT reset — returning to the
    // original anchor is still a remount, still needs restoreFromUrl to
    // stay false (see the hasEverReanchored declaration above).
    setCurrentAnchorId(anchorNodeId);
    void load(anchorNodeId);
  };

  const handleRetry = () => {
    if (currentAnchorId) void load(currentAnchorId);
  };

  if (!isFeatureEnabled('PKG_NEIGHBOURHOOD_PANEL_ENABLED')) return null;
  if (!anchorNodeId) return null;

  const isExpanded = status !== 'collapsed';
  const isReanchored = isExpanded && currentAnchorId !== anchorNodeId;

  return (
    <div style={{ marginTop: 'var(--space-4, 16px)', borderTop: '1px solid var(--border)', paddingTop: 'var(--space-3, 12px)' }}>
      <button
        type="button"
        onClick={handleToggle}
        aria-expanded={isExpanded}
        style={{
          background: 'transparent', border: 'none', padding: 0, cursor: 'pointer',
          fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)',
          display: 'flex', alignItems: 'center', gap: 6,
        }}
      >
        <span>{isExpanded ? '▾' : '▸'}</span>
        {isExpanded ? 'Hide graph' : 'Show graph'}
      </button>

      {isExpanded && (
        <div style={{ marginTop: 'var(--space-3, 12px)' }}>
          {status === 'loading' && (
            <p style={{ fontSize: 13, color: 'var(--text-secondary)' }}>Loading neighbourhood…</p>
          )}

          {status === 'unavailable' && (
            <div>
              <p style={{ fontSize: 13, color: 'var(--error, #ef4444)' }}>Could not load graph.</p>
              <button type="button" onClick={handleRetry} style={{ fontSize: 12, color: 'var(--primary)', background: 'transparent', border: 'none', cursor: 'pointer', padding: 0 }}>
                Retry
              </button>
            </div>
          )}

          {status === 'empty' && (
            <p style={{ fontSize: 13, color: 'var(--text-secondary)' }}>No connected entities yet.</p>
          )}

          {status === 'ready' && data && (
            <div>
              {isReanchored && (
                <button
                  type="button"
                  onClick={handleReset}
                  style={{ fontSize: 12, color: 'var(--primary)', background: 'transparent', border: 'none', cursor: 'pointer', padding: 0, marginBottom: 8 }}
                >
                  ← Back to original node
                </button>
              )}
              {/* PKGV.A3 (#794) — keyed on currentAnchorId (V1, locked):
                  React remounts this on every re-anchor, which IS the
                  reset mechanism for L6 ("reset filters on re-anchor").
                  data (the fetched neighbourhood) stays here in the
                  parent; the child only ever receives it as props.
                  restoreFromUrl={!hasEverReanchored}, NOT !isReanchored
                  (R2 adversarial fix, item 4): only the single pristine
                  first-ever mount of this row consults the URL. Using
                  isReanchored instead would flip restoreFromUrl back to
                  true the moment the user clicks "Back to original
                  node" (currentAnchorId returns to anchorNodeId),
                  re-exposing the exact setSearchParams commit-timing
                  race this component already fixed for the outbound
                  re-anchor — hasEverReanchored is one-way and never
                  resets, so every remount after the first stays on pure
                  defaults regardless of which anchor it lands on. */}
              <PKGFilterableNeighbourhood
                key={currentAnchorId}
                nodes={data.nodes}
                edges={data.edges}
                layoutHint="radial"
                interactionLevel={interactionLevel}
                centerNodeId={currentAnchorId ?? undefined}
                onNodeClick={handleNodeClick}
                restoreFromUrl={!hasEverReanchored}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
}
